import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ITealiumAnalyticsOptions, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { SortDirection } from '@ddt-web/common';
import { CellData } from '@ddt-web/data-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest } from 'rxjs';

import { TableSearchService } from '../services/table-search.service';
import { TableSortService } from '../services/table-sort.service';
import { TableDataUtil } from '../utils';

/**
 * @description Used as:
 * <app-table [data]="tableData"></app-table>
 * or within other flags:
 * <app-table [data]="tableData" [displayedColumns]="['ticker', 'name', 'as of date']" [isRowExpandable]="true" [isSortable]="true" [stickyColumnIndex]="[0]"></app-table>
 */
@UntilDestroy()
@Component({
	selector: 'ac-sticky-table',
	templateUrl: './sticky-table.component.html',
	styleUrls: ['./sticky-table.component.scss'],
	animations: [
		trigger('detailExpand', [
			state('collapsed, void', style({ height: '0px', minHeight: '0' })),
			state('expanded', style({ height: '*' })),
			transition('expanded <=> collapsed, void <=> expanded', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
		])
	]
})
export class StickyTableComponent implements OnChanges, OnInit {
	@Input() public isSortable?: boolean = true;
	@Input() public stickyColumnIndex?: number[] = [0];
	@Input() public dataArray: Array<{ [key: string]: CellData }>;
	@Input() public displayedColumns?: string[];
	@Input() public columnsAlwaysToShow?: string[] = [];
	@Input() public showInfoBubble?: boolean = true;
	@Input() public showPagination?: boolean = false;
	@Input() public defaultSortKey?: string;
	@Input() public defaultSortDirection: number = 1;
	@Input() public searchableColumns?: string[];
	@Input() public infoBubbleMappings?: Array<{ [key: string]: string }>;
	@Input() public showSearchBar?: boolean = true;
	@Input() public showColumnRenderer?: boolean = true;
	// TODO: This feature is still in progress
	@Input() public isRowExpandable?: boolean = false;

	public tableDataSource$ = new BehaviorSubject<any[]>([]);
	public currentPage$ = new BehaviorSubject<number>(1);
	public pageSize$ = new BehaviorSubject<number>(0);
	public dataOnPage$ = new BehaviorSubject<any[]>([]);
	public searchFormControl = new UntypedFormControl();
	public sortKey$ = new BehaviorSubject<string>('');
	public sortDirection$ = new BehaviorSubject<number>(0);
	public expandedElement;
	public columnsObject;
	public columnsToHideIndex = [];
	public showiSharesIdeasInfoBubbleModal: boolean = false;
	public iSharesIdeasInfoBubbleText: string;

	constructor(
		private tableSearchService: TableSearchService,
		private tableSortService: TableSortService,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	public ngOnInit(): void {
		combineLatest(this.tableDataSource$, this.currentPage$, this.pageSize$)
			.pipe(untilDestroyed(this))
			.subscribe(([allSources, currentPage, pageSize]) => {
				const startingIndex = (currentPage - 1) * pageSize;
				const onPage = allSources.slice(startingIndex, startingIndex + pageSize);
				this.dataOnPage$.next(onPage);
			});
		combineLatest(this.searchFormControl.valueChanges, this.sortKey$, this.sortDirection$)
			.pipe(untilDestroyed(this))
			.subscribe(([searchTerm, sortKey, sortDirection]) => {
				const filteredData = this.tableSearchService.searchDataArray(this.dataArray, this.searchableColumns, searchTerm);
				let sortedData = this.tableSortService.sortDataArray(filteredData, sortKey, sortDirection);
				sortedData = TableDataUtil.addColumnSeparators(this.columnsToHideIndex, sortedData);
				this.tableDataSource$.next(sortedData);

			});
		this.searchFormControl.setValue('');
	}

	public ngOnChanges(): void {
		if (this.dataArray) {
			this.columnsObject = this.columnsObject || TableDataUtil.getColumnsGroupObject(this.dataArray);
			this.displayedColumns = Object.keys(this.dataArray[0]);
			this.changeColumns(TableDataUtil.getDisplayedColumns(this.columnsObject));
			this.pageSize$.next(this.dataArray.length);
			this.tableDataSource$.next(this.dataArray);
			if (this.defaultSortKey) {
				this.sortKey$.next(this.defaultSortKey);
			}
			if (this.defaultSortDirection) {
				this.sortDirection$.next(this.defaultSortDirection);
			}
		}
	}

	public adjustSort(key: string): void {
		if (this.sortKey$.value === key) {
			if (this.sortDirection$.value === SortDirection.ASC) {
				this.sortDirection$.next(SortDirection.DESC);
			} else {
				this.sortDirection$.next(SortDirection.ASC);
			}
			return;
		}
		this.sortKey$.next(key);
		this.sortDirection$.next(SortDirection.ASC);
	}

	public expandRow(element): void {
		if (this.isRowExpandable) {
			this.expandedElement = this.expandedElement === element ? null : element;
		}
	}

	/**
	 *
	 * @param columns array of column names to show in table
	 * Transfer name array into index array so we know what column index is the ones we want to show,
	 * then we use displayedColumns which is all the columns we want to show to find the column indexes we want to hide
	 */
	public changeColumns(columns: string[]): void {
		this.columnsToHideIndex = [];
		const columnsToShowIndex = this.stickyColumnIndex.concat(columns.map(column => this.displayedColumns.indexOf(column)));
		this.displayedColumns.forEach((column, index) => {
			if (!columnsToShowIndex.includes(index)) {
				this.columnsToHideIndex.push(index);
			}
		});
		this.dataArray = TableDataUtil.clearColumnSeparators(this.displayedColumns, this.dataArray);
		this.tableDataSource$.next(TableDataUtil.addColumnSeparators(this.columnsToHideIndex, this.dataArray));
	}

	public setColumnObject(columnsObject: { [key: string]: { [key: string]: string | Object } }): void {
		this.columnsObject = columnsObject;
	}

	/**
	 * Sets the text of the iShares Ideas modal and opens it.
	 *
	 * @param {string} trackId id used to track the event
	 * @param {*} infoBubble infoBubble text.
	 */
	public toggleInfoDialog(trackId: string, infoBubble: any): void {
		const analyticData: ITealiumAnalyticsOptions = { linkName: 'tooltip_' + trackId };
		const event = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.TOOL_TIP, analyticData);
		this.tealiumAnalyticsService.trackEventTealium(event);

		this.iSharesIdeasInfoBubbleText = infoBubble;
		this.showiSharesIdeasInfoBubbleModal = !this.showiSharesIdeasInfoBubbleModal;
	}
}
